import { useNavigate } from 'react-router-dom';
import { BarChart3, CalendarDays, ChevronRight, MapPin, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useMyRankingEntry } from '@/hooks/useGlobalRankings';
import type { GroupListItem } from '@/hooks/useUserGroupsOptimized';
import { ROUTES } from '@/constants/routes';

interface RailPoll {
  id: string;
  title: string;
  group_name: string;
  user_response?: 'available' | 'unavailable';
}

interface DesktopUtilityRailProps {
  polls: RailPoll[];
  groups: GroupListItem[];
  onRespond: (pollId: string, response: 'available' | 'unavailable') => Promise<boolean>;
}

export function DesktopUtilityRail({ polls, groups, onRespond }: DesktopUtilityRailProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: ranking } = useMyRankingEntry(user?.id);
  const invitations = groups.filter(group => group.invitationStatus === 'pending');
  const featuredPoll = polls[0];

  return (
    <aside className="hidden xl:block space-y-4" aria-label="Your activity overview">
      {featuredPoll && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> Active polls</span>
              <Badge variant="secondary">{polls.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm font-semibold line-clamp-2">{featuredPoll.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{featuredPoll.group_name}</p>
            </div>
            {featuredPoll.user_response ? (
              <p className="text-xs text-muted-foreground">Your response: <span className="font-medium capitalize text-foreground">{featuredPoll.user_response}</span></p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button size="sm" className="min-h-11" onClick={() => void onRespond(featuredPoll.id, 'available')}>Available</Button>
                <Button size="sm" variant="outline" className="min-h-11" onClick={() => void onRespond(featuredPoll.id, 'unavailable')}>Unavailable</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {ranking && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><BarChart3 className="h-4 w-4 text-primary" /> Your global ranking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-end justify-between">
              <div><span className="text-3xl font-bold tabular-nums">#{ranking.rank}</span><p className="text-xs text-muted-foreground">Worldwide</p></div>
              <div className="text-right"><span className="text-lg font-bold tabular-nums">{ranking.skill_rating.toFixed(2)}</span><p className="text-xs text-muted-foreground">Skill rating</p></div>
            </div>
            <Button variant="outline" className="min-h-11 w-full justify-between" onClick={() => navigate(ROUTES.RANKINGS)}>
              Full rankings <ChevronRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {invitations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Group invitations</span>
              <Badge>{invitations.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {invitations.slice(0, 3).map(group => (
              <Button key={group.id} variant="ghost" className="min-h-11 w-full justify-between px-2" onClick={() => navigate(ROUTES.GROUP_DETAIL(group.id))}>
                <span className="truncate">{group.name}</span><ChevronRight className="h-4 w-4 shrink-0" />
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      <Button variant="outline" className="min-h-11 w-full justify-between" onClick={() => navigate('/padel-courts-near-me')}>
        <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Find padel courts</span>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </aside>
  );
}